/**
 * Sidebar Component
 * Navigation sidebar with React Router integration
 */
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { LayoutDashboard, BarChart2, GitBranch, Settings, Search, Store, DollarSign, Sun, Moon, Languages, Sparkles, LogOut, User, Loader2, ShieldAlert, X, FileSearch, Clock, CreditCard } from 'lucide-react';
import { cn } from '../ui/utils';
import brandLogo from '../../assets/bow-sales.png';
import brandLogoDark from '../../assets/bow-sales-dark.png';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { useBilling } from '../../contexts/BillingContext';
import { useViewMode } from '../../hooks/useViewMode';
import { useExpertMode } from '../../hooks/useExpertMode';
import { systemAPI, type SystemStatusResponse } from '../../services/api';
import { UserRole, getRoleName } from '../../hooks/usePermissions';
import { useAdBlockDetection } from '../../hooks/useAdBlockDetection';
import { Button } from '../ui/button';
import { ROUTES } from '../../router';
import { CreditBadge } from '../molecules/CreditBadge';
import { formatRelativeTime } from '../../utils/dateFormat';
import { logger } from '../../lib/logger';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";

// Navigation item type
interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  roles: UserRole[];
  condition?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useTranslation();
  const { user, logout, apiKey } = useAuth();
  const { billingVisible } = useBilling();
  const { mode } = useViewMode();
  const isExpert = mode === 'expert';

  // System status state
  const [systemStatus, setSystemStatus] = useState<SystemStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  // Ad blocker detection
  const { isBlocked: adBlockDetected, dismissed: adBlockDismissed, dismiss: dismissAdBlockWarning } = useAdBlockDetection();

  // Poll system status every 10 seconds
  useEffect(() => {
    const fetchStatus = async () => {
      if (!user || !apiKey) return;
      try {
        const status = await systemAPI.getStatus(apiKey);
        setSystemStatus(status);
      } catch (error) {
        logger.error('Failed to fetch system status:', error);
      } finally {
        setStatusLoading(false);
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, [user, apiKey]);


  // Navigation — same for all users, only differs by easy/expert mode.
  // No tier-based gating. Credits control what you can afford — not what you can see.
  const navGroups: NavGroup[] = [
    {
      label: t('nav.groupOverview'),
      items: [
        { path: ROUTES.DASHBOARD, label: t('nav.dashboard'), icon: LayoutDashboard, roles: [UserRole.ADMIN, UserRole.USER, UserRole.VIEWER] },
        { path: ROUTES.ANALYSIS, label: t('nav.analysis'), icon: BarChart2, roles: [UserRole.ADMIN, UserRole.USER, UserRole.VIEWER] },
      ],
    },
    {
      label: t('nav.groupData'),
      items: [
        { path: ROUTES.SELLERS, label: t('nav.sellers'), icon: Store, roles: [UserRole.ADMIN, UserRole.USER, UserRole.VIEWER] },
        { path: ROUTES.DISCOVERY, label: t('nav.discovery'), icon: Search, roles: [UserRole.ADMIN, UserRole.USER] },
        { path: ROUTES.CONTENT_DISCOVERY, label: t('nav.contentDiscovery'), icon: FileSearch, roles: [UserRole.ADMIN, UserRole.USER], condition: isExpert },
      ],
    },
    {
      label: t('nav.groupExecution'),
      items: [
        { path: ROUTES.PRICING, label: t('nav.pricing'), icon: DollarSign, roles: [UserRole.ADMIN, UserRole.USER] },
        { path: ROUTES.WORKFLOW, label: t('nav.workflow'), icon: GitBranch, roles: [UserRole.ADMIN, UserRole.USER] },
        { path: ROUTES.SCHEDULES, label: t('nav.schedules'), icon: Clock, roles: [UserRole.ADMIN] },
      ],
    },
    {
      label: t('nav.groupTools'),
      items: [
        { path: ROUTES.AI_ADVISOR, label: t('nav.aiAdvisor'), icon: Sparkles, roles: [UserRole.ADMIN, UserRole.USER, UserRole.VIEWER], condition: isExpert },
        { path: ROUTES.BILLING, label: t('nav.billing'), icon: CreditCard, roles: [UserRole.ADMIN], condition: billingVisible },
        { path: ROUTES.SETTINGS, label: t('nav.settings'), icon: Settings, roles: [UserRole.ADMIN, UserRole.USER, UserRole.VIEWER] },
      ],
    },
  ];

  // Filter navigation groups based on user role (remove empty groups)
  const visibleNavGroups = navGroups
    .map(group => ({
      ...group,
      items: group.items.filter(item =>
        user &&
        item.roles.includes(user.role?.toUpperCase() as UserRole) &&
        (item.condition === undefined || item.condition)
      ),
    }))
    .filter(group => group.items.length > 0);

  // Flat list for mobile nav
  const visibleNavItems = visibleNavGroups.flatMap(group => group.items);

  // Check if a path is active (including sub-routes)
  const isPathActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Handle logout and redirect
  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN);
  };

  // Mobile Bottom Navigation (Floating Island Style)
  const MobileNav = () => (
    <div className="md:hidden fixed bottom-6 left-4 right-4 z-50">
      <div className="bg-white/90 dark:bg-[#111]/90 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl shadow-2xl p-2 flex items-center justify-between">
        {visibleNavItems.slice(0, 3).map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) => cn(
              "flex flex-col items-center justify-center p-3 flex-1 rounded-xl transition-all duration-200 relative",
              isActive || isPathActive(item.path)
                ? "text-fuchsia-600 dark:text-fuchsia-400 bg-fuchsia-50 dark:bg-fuchsia-500/10"
                : "text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5"
            )}
          >
            <item.icon className={cn("w-5 h-5 mb-1", isPathActive(item.path) && "fill-current")} />
            <span className="text-[10px] font-medium truncate max-w-full leading-none">{item.label}</span>
            {isPathActive(item.path) && (
                <span className="absolute -bottom-1 w-1 h-1 rounded-full bg-fuchsia-600 dark:bg-fuchsia-400" />
            )}
          </NavLink>
        ))}

        {/* More Menu for Mobile */}
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button className={cn(
                    "flex flex-col items-center justify-center p-3 flex-1 rounded-xl transition-all duration-200 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5",
                    !visibleNavItems.slice(0, 3).some(item => isPathActive(item.path)) && "text-fuchsia-600 dark:text-fuchsia-400"
                )}>
                    <div className="w-5 h-5 mb-1 flex items-center justify-center space-x-0.5">
                        <div className="w-1 h-1 rounded-full bg-current" />
                        <div className="w-1 h-1 rounded-full bg-current" />
                        <div className="w-1 h-1 rounded-full bg-current" />
                    </div>
                    <span className="text-[10px] font-medium leading-none">More</span>
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-white/95 dark:bg-[#111]/95 backdrop-blur-xl border-gray-200 dark:border-white/10 mb-4">
                 {visibleNavItems.slice(3).map((item) => (
                     <DropdownMenuItem
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        className={cn(
                            "cursor-pointer transition-colors duration-200",
                            isPathActive(item.path)
                                ? "text-fuchsia-600 dark:text-fuchsia-400 bg-fuchsia-50 dark:bg-fuchsia-500/10"
                                : "text-gray-700 dark:text-gray-300 hover:text-fuchsia-600 dark:hover:text-fuchsia-400 focus:text-fuchsia-600 dark:focus:text-fuchsia-400 focus:bg-fuchsia-50 dark:focus:bg-fuchsia-500/10"
                        )}
                    >
                        <item.icon className="w-4 h-4 mr-2" />
                        {item.label}
                     </DropdownMenuItem>
                 ))}
                 <DropdownMenuSeparator className="bg-gray-200 dark:bg-white/10" />
                 <div className="flex items-center justify-between px-2 py-1.5">
                    <Button variant="ghost" size="sm" onClick={toggleTheme} className="h-8 w-8 p-0">
                        {theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setLanguage(language === 'en' ? 'de' : 'en')} className="h-8 px-2 text-xs">
                        {language.toUpperCase()}
                    </Button>
                 </div>
            </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  // Desktop Sidebar
  return (
    <>
        <div className="hidden md:flex w-64 h-full bg-gray-50 dark:bg-[#050505] border-r border-gray-200 dark:border-white/10 flex-col transition-colors duration-300">
        <div className="flex items-center justify-center py-4">
            <div className="h-16 w-48 flex items-center justify-center">
            <img
                src={brandLogoDark}
                alt="BOW Sales Logo"
                className="h-full w-full object-contain dark:hidden"
            />
            <img
                src={brandLogo}
                alt="BOW Sales Logo"
                className="h-full w-full object-contain hidden dark:block"
            />
            </div>
        </div>

        <nav className="flex-1 px-3 py-4 overflow-y-auto scrollbar-hide">
            {visibleNavGroups.map((group, groupIndex) => (
            <div key={group.label} className={groupIndex > 0 ? 'mt-4' : ''}>
                <div className="px-4 pt-1 pb-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-600">
                    {group.label}
                  </span>
                </div>
                <div className="space-y-1">
                {group.items.map((item) => (
                <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) => cn(
                    "w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group",
                    isActive || isPathActive(item.path)
                        ? "bg-fuchsia-50 dark:bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400 shadow-sm dark:shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)] border border-fuchsia-200 dark:border-fuchsia-500/20"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5"
                    )}
                >
                    <item.icon
                    className={cn(
                        "w-5 h-5 transition-colors",
                        isPathActive(item.path) ? "text-fuchsia-600 dark:text-fuchsia-400" : "text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300"
                    )}
                    />
                    <span className="font-medium">{item.label}</span>
                    {isPathActive(item.path) && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-fuchsia-600 dark:bg-fuchsia-400 shadow-[0_0_8px_rgba(192,38,211,0.4)] dark:shadow-[0_0_8px_rgba(192,38,211,0.8)]" />
                    )}
                </NavLink>
                ))}
                </div>
            </div>
            ))}
        </nav>

        <div className="p-4 border-t border-gray-200 dark:border-white/10 space-y-4">
            {/* User Info */}
            {user && (
              <div className="p-3 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-fuchsia-900/20 dark:to-purple-900/20 border border-gray-200 dark:border-white/5">
                <button
                  onClick={() => navigate(ROUTES.PROFILE)}
                  className="w-full flex items-center gap-3 mb-2 hover:opacity-80 transition-opacity cursor-pointer group"
                >
                  <div className="w-9 h-9 rounded-full bg-fuchsia-500 dark:bg-fuchsia-600 flex items-center justify-center group-hover:ring-2 group-hover:ring-fuchsia-400 transition-all shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate group-hover:text-fuchsia-600 dark:group-hover:text-fuchsia-400 transition-colors">{user.name}</p>
                    <span className="inline-block mt-0.5 px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-200 dark:bg-white/10 text-gray-500 dark:text-gray-400">{getRoleName(user.role as UserRole)}</span>
                  </div>
                </button>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  size="sm"
                  className="w-full h-8 border-gray-300 dark:border-white/20 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-500/30"
                >
                  <LogOut className="w-3.5 h-3.5 mr-2" />
                  Logout
                </Button>
              </div>
            )}

            {/* System Status */}
            <CreditBadge />
            <div className="p-4 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-900/20 dark:to-gray-800/20 border border-gray-200 dark:border-white/5">
            {statusLoading ? (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-2">
                    <div className={cn(
                      "w-2 h-2 rounded-full",
                      systemStatus === null
                        ? "bg-gray-400"
                        : systemStatus.running_jobs > 0
                          ? "bg-yellow-500 animate-pulse"
                          : systemStatus.is_active
                            ? "bg-green-500"
                            : "bg-red-500"
                    )} />
                    <span className={cn(
                      "text-xs font-medium",
                      systemStatus === null
                        ? "text-gray-500 dark:text-gray-400"
                        : systemStatus.running_jobs > 0
                          ? "text-yellow-600 dark:text-yellow-400"
                          : systemStatus.is_active
                            ? "text-green-600 dark:text-green-400"
                            : "text-red-600 dark:text-red-400"
                    )}>
                      {systemStatus === null
                        ? 'Connecting...'
                        : systemStatus.running_jobs > 0
                          ? `${systemStatus.running_jobs} Job(s) Running`
                          : systemStatus.is_active
                            ? t('dashboard.systemActive')
                            : 'System Inactive'}
                    </span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-500">
                    {t('dashboard.lastCrawl')}: {formatRelativeTime(systemStatus?.last_execution || null, ' ' + t('common.ago'))}
                </div>
                {systemStatus?.last_job_name && (
                  <div className="text-xs text-gray-400 dark:text-gray-600 truncate mt-0.5">
                    {systemStatus.last_job_name}
                  </div>
                )}
              </>
            )}
            </div>

            {/* Ad Blocker Warning */}
            {adBlockDetected && !adBlockDismissed && (
              <div className="p-3 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-500/20">
                <div className="flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
                      {language === 'de' ? 'Browser-Erweiterung erkannt' : 'Browser Extension Detected'}
                    </p>
                    <p className="text-xs text-amber-600/80 dark:text-amber-400/80 mt-1">
                      {language === 'de'
                        ? 'Ein Werbeblocker blockiert möglicherweise Fehlerberichte. Bitte diese Seite auf die Whitelist setzen.'
                        : 'An ad blocker may be blocking error reports. Please whitelist this site.'}
                    </p>
                  </div>
                  <button
                    onClick={dismissAdBlockWarning}
                    className="p-1 rounded hover:bg-amber-200/50 dark:hover:bg-amber-800/30 text-amber-600 dark:text-amber-400 transition-colors"
                    aria-label="Dismiss"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
                <Button
                    variant="outline"
                    size="icon"
                    onClick={toggleTheme}
                    className="flex-1 h-9 border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-400"
                >
                    {theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                </Button>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="outline"
                            className="flex-1 h-9 border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-400 text-xs font-medium"
                        >
                            <Languages className="w-3.5 h-3.5 mr-2" />
                            {language === 'en' ? 'English' : 'Deutsch'}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-36 bg-white dark:bg-[#111] border-gray-200 dark:border-white/10">
                        <DropdownMenuItem onClick={() => setLanguage('en')} className="cursor-pointer">
                            <span className="flex items-center gap-2">
                                {language === 'en' && <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500" />}
                                English
                            </span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setLanguage('de')} className="cursor-pointer">
                            <span className="flex items-center gap-2">
                                {language === 'de' && <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500" />}
                                Deutsch
                            </span>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
        </div>
        <MobileNav />
    </>
  );
}

export default Sidebar;
